// Nudges Component for Dashboard
import React, { useState, useEffect } from 'react';
import { fmtDatetime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Bell, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const Nudges = () => {
  const navigate = useNavigate();
  const { userTimezone } = useAuth();
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNudges();
  }, []);

  const fetchNudges = async () => {
    try {
      const response = await api.get('/nudges/my');
      // Filter out signature nudges for already-executed documents
      const nudges = response.data || [];
      const filtered = await Promise.all(nudges.map(async n => {
        if (n.type === 'contract_signature_required' && n.entity_id) {
          try {
            const d = await api.get(`/sale-owner-deliverables/${n.entity_id}`);
            if (d.data?.executed_file_id || d.data?.executed_at) return null;
          } catch { /* keep nudge */ }
        }
        return n;
      }));
      setNudges(filtered.filter(Boolean));
    } catch (error) {
      console.error('Error fetching nudges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNudgeClick = async (nudge) => {
    try {
      await api.put(`/nudges/${nudge.id}/mark-read`);
      setNudges(prev => prev.map(n => n.id === nudge.id ? { ...n, is_read: true } : n));

      if (nudge.type === 'contract_signature_required' && nudge.entity_id) {
        // Check if already executed — if so, go to deliverable page not sign page
        try {
          const d = await api.get(`/sale-owner-deliverables/${nudge.entity_id}`);
          if (d.data?.executed_file_id || d.data?.executed_at) {
            // Already executed — navigate to the opportunity deliverables tab
            const oppId = d.data?.sale_opportunity_id;
            if (oppId) { navigate(`/sales/opportunities/${oppId}?tab=deliverables`); return; }
          }
        } catch { /* fall through to default link */ }
        navigate(nudge.link || `/contract-sign/${nudge.entity_id}`);
      } else if (nudge.link) {
        navigate(nudge.link);
      } else if (nudge.sale_opportunity_id) {
        navigate(`/sales/opportunities/${nudge.sale_opportunity_id}`);
      }
    } catch (error) {
      console.error('Error marking nudge as read:', error);
    }
  };

  const handleDismiss = async (nudgeId, e) => {
    e.stopPropagation();
    
    try {
      await api.delete(`/nudges/${nudgeId}`);
      setNudges(prev => prev.filter(n => n.id !== nudgeId));
      toast.success('Nudge dismissed');
    } catch (error) {
      console.error('Error dismissing nudge:', error);
      toast.error('Failed to dismiss nudge');
    }
  };

  const unreadCount = nudges.filter(n => !n.is_read).length;

  // Don't render the card if there are no nudges
  if (!loading && nudges.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Nudges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Nudges
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2">
              {unreadCount} new
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {nudges.length === 0 ? (
          <div className="text-center py-6">
            <Bell className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-20" />
            <p className="text-sm text-muted-foreground">No nudges yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {nudges.slice(0, 5).map(nudge => (
              <div
                key={nudge.id}
                onClick={() => handleNudgeClick(nudge)}
                className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                  nudge.is_read ? 'bg-muted/20 opacity-75' : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {nudge.type === 'contract_signature_required' ? (
                        <>Document for Signature</>
                      ) : (
                        <>
                          <span className="text-primary">{nudge.sender_name}</span> has nudged you to complete{' '}
                          <span className="font-semibold">{nudge.deliverable_name}</span>
                        </>
                      )}
                    </p>
                    {nudge.message && (nudge.type === 'contract_signature_required' || nudge.message !== `Please complete the ${nudge.deliverable_name} deliverable.`) && (
                      <p className="text-xs text-muted-foreground mt-1 italic">"{nudge.message}"</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {nudge.created_at ? fmtDatetime(nudge.created_at, userTimezone) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!nudge.is_read && (
                      <Badge variant="destructive" className="text-xs">New</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDismiss(nudge.id, e)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {nudges.length > 5 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => navigate('/nudges')}
              >
                View All {nudges.length} Nudges
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Nudges;
